import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../../components/Layout';
import AccessoNegato from '../../components/permessi/AccessoNegato';
import {
  FlagsPermesso, CampoPermesso, OriginePermesso,
  TUTTO_OFF, normalizzaFlags, flagsUguali,
} from '../../components/permessi/GrigliaPermessi';
import { usePermesso } from '../../context/PermessiContext';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { apiFetch } from '../../lib/api';
import '../../styles/impostazioni.css';

// Vista trasposta: voci in riga, utenti in colonna.
//
// Risolve il caso che nella pagina per-utente costa una schermata a testa:
// "ho rilasciato Fatture fornitori, accendila a queste otto persone".

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface UtenteCol {
  id:      number;
  nome:    string;
  email:   string;
  profilo: { id: number; nome: string } | null;
}

interface ModuloRiga {
  modulo: string;
  label:  string;
  macro:  string | null;
  gruppo: string | null;
}

interface CellaApi extends FlagsPermesso {
  origine: OriginePermesso;
}

interface Matrice {
  utenti:   UtenteCol[];
  moduli:   ModuloRiga[];
  macro:    string[];
  permessi: Record<string, Record<string, CellaApi>>;
}

const CAMPI: { valore: CampoPermesso; label: string }[] = [
  { valore: 'canView',   label: 'Visualizza' },
  { valore: 'canEdit',   label: 'Modifica' },
  { valore: 'canDelete', label: 'Elimina' },
  { valore: 'canExport', label: 'Esporta' },
];

const chiave = (utenteId: number, modulo: string) => `${utenteId}|${modulo}`;

// ── Componente ────────────────────────────────────────────────────────────────

export default function PermessiMatricePage() {
  const { isSuperadmin } = usePermesso();
  const { societaId, setSocietaId, listaSocieta, isUnica, loading: loadingSoc } = useFiltroSocieta();

  // La matrice confronta gli utenti di UNA società: "Tutte le società" non ha
  // significato qui, serve sempre un id concreto.
  const sid = societaId ? Number(societaId) : null;

  const [macro,  setMacro]  = useState('');
  const [campo,  setCampo]  = useState<CampoPermesso>('canView');

  const [dati,     setDati]     = useState<Matrice | null>(null);
  const [modifiche, setModifiche] = useState<Record<string, FlagsPermesso>>({});

  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState('');
  const [error,   setError]   = useState('');

  const nModifiche = Object.keys(modifiche).length;

  // ── Caricamento ─────────────────────────────────────────────────────────
  const carica = useCallback(async (sid: number, macroFiltro: string) => {
    setLoading(true);
    setError('');
    setMsg('');
    setModifiche({});
    try {
      const qs = new URLSearchParams({ societaId: String(sid) });
      if (macroFiltro) qs.set('macro', macroFiltro);
      // @api GET /api/permessi/matrice?societaId=N&macro=S -> {"success":"true","data":"Matrice"}
      const d = await apiFetch<{ data: Matrice }>(`/api/permessi/matrice?${qs.toString()}`);
      setDati(d.data);
    } catch (e: any) { setError(e.message); setDati(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isSuperadmin || !sid) return;
    carica(sid, macro);
  }, [isSuperadmin, sid, macro, carica]);

  // ── Lettura valore corrente di una cella (modifica pendente o dato) ─────
  const valoreCella = useCallback((utenteId: number, modulo: string): FlagsPermesso => {
    const pendente = modifiche[chiave(utenteId, modulo)];
    if (pendente) return pendente;
    const cella = dati?.permessi[utenteId]?.[modulo];
    return cella
      ? { canView: cella.canView, canEdit: cella.canEdit, canDelete: cella.canDelete, canExport: cella.canExport }
      : { ...TUTTO_OFF };
  }, [modifiche, dati]);

  const valoreOriginale = useCallback((utenteId: number, modulo: string): FlagsPermesso => {
    const cella = dati?.permessi[utenteId]?.[modulo];
    return cella
      ? { canView: cella.canView, canEdit: cella.canEdit, canDelete: cella.canDelete, canExport: cella.canExport }
      : { ...TUTTO_OFF };
  }, [dati]);

  // ── Modifica ────────────────────────────────────────────────────────────
  // Registra la modifica solo se differisce dal dato caricato: rimettere il
  // valore com'era fa sparire la cella dal salvataggio.
  const applica = useCallback((celle: { utenteId: number; modulo: string }[], valore: boolean) => {
    setModifiche(prev => {
      const next = { ...prev };
      for (const { utenteId, modulo } of celle) {
        const k = chiave(utenteId, modulo);
        const attuale = next[k] ?? valoreOriginale(utenteId, modulo);
        const nuovo   = normalizzaFlags({ ...attuale, [campo]: valore });
        if (flagsUguali(nuovo, valoreOriginale(utenteId, modulo))) delete next[k];
        else next[k] = nuovo;
      }
      return next;
    });
  }, [campo, valoreOriginale]);

  const toggleCella = (utenteId: number, modulo: string) => {
    applica([{ utenteId, modulo }], !valoreCella(utenteId, modulo)[campo]);
  };

  const applicaRiga = (modulo: string, valore: boolean) => {
    if (!dati) return;
    applica(dati.utenti.map(u => ({ utenteId: u.id, modulo })), valore);
  };

  const applicaColonna = (utenteId: number, valore: boolean) => {
    if (!dati) return;
    applica(dati.moduli.map(m => ({ utenteId, modulo: m.modulo })), valore);
  };

  // ── Salvataggio ─────────────────────────────────────────────────────────
  const salva = async () => {
    if (!sid || !nModifiche) return;
    setSaving(true);
    setMsg('');
    setError('');
    try {
      const payload = Object.entries(modifiche).map(([k, flags]) => {
        const [utenteId, modulo] = [Number(k.split('|')[0]), k.slice(k.indexOf('|') + 1)];
        return { utenteId, modulo, ...flags };
      });
      // @api PUT /api/permessi/matrice {"societaId":"number","modifiche":"Cella[]"} -> {"success":"true","data":"{utentiAggiornati:number,celleAggiornate:number}"}
      const r = await apiFetch<{ data: { utentiAggiornati: number; celleAggiornate: number } }>(
        '/api/permessi/matrice',
        { method: 'PUT', body: JSON.stringify({ societaId: sid, modifiche: payload }) },
      );
      setMsg(`${r.data.celleAggiornate} permessi salvati su ${r.data.utentiAggiornati} utenti.`);
      await carica(sid, macro);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  // ── Righe con separatore di gruppo ──────────────────────────────────────
  const righe = useMemo(() => {
    if (!dati) return [];
    const out: ({ tipo: 'gruppo'; label: string } | { tipo: 'voce'; voce: ModuloRiga })[] = [];
    let gruppoCorrente: string | null = null;
    for (const m of dati.moduli) {
      const etichetta = `${m.macro ?? 'Altri permessi'} › ${m.gruppo ?? '—'}`;
      if (etichetta !== gruppoCorrente) {
        gruppoCorrente = etichetta;
        out.push({ tipo: 'gruppo', label: etichetta });
      }
      out.push({ tipo: 'voce', voce: m });
    }
    return out;
  }, [dati]);

  if (!isSuperadmin) {
    return (
      <Layout>
        <AccessoNegato messaggio="Solo un Superadmin può gestire i permessi degli utenti." />
      </Layout>
    );
  }

  const nomeSocieta = listaSocieta.find(s => String(s.id) === societaId)?.nome ?? '';

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Permessi per voce</h2>
          <p>
            Voci di menu in riga, utenti in colonna: accendi una voce a più persone
            in una schermata sola. Le celle su sfondo ambra sono deroghe al profilo.
          </p>
        </div>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}
      {msg   && <div className="bop-inline-success">{msg}</div>}

      {/* ── Filtri ────────────────────────────────────────────────────── */}
      <div className="bop-card impostazioni-permessi-selettori">
        <div className="impostazioni-permessi-selettori-row">
          {/* Combo visibile solo quando la topbar è su "Tutte le società": la
              matrice ha bisogno di una società concreta per confrontare utenti. */}
          {!isUnica && (
            <div className="impostazioni-permessi-selettori-group">
              <label className="impostazioni-permessi-label">Società:</label>
              <select
                className="bop-select impostazioni-permessi-select-societa"
                value={societaId}
                onChange={e => setSocietaId(e.target.value)}
                disabled={loadingSoc}
              >
                <option value="">— Seleziona una società —</option>
                {listaSocieta.map(s => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="impostazioni-permessi-selettori-group">
            <label className="impostazioni-permessi-label">Sezione:</label>
            <select
              className="bop-select impostazioni-permessi-select-utente"
              value={macro}
              onChange={e => setMacro(e.target.value)}
              disabled={loading || !dati}
            >
              <option value="">— Tutte le sezioni —</option>
              {(dati?.macro ?? []).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="impostazioni-permessi-selettori-group">
            <label className="impostazioni-permessi-label">Permesso:</label>
            <select
              className="bop-select impostazioni-matrice-azione-select"
              value={campo}
              onChange={e => setCampo(e.target.value as CampoPermesso)}
              disabled={loading}
            >
              {CAMPI.map(c => (
                <option key={c.valore} value={c.valore}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loadingSoc ? (
        <div className="bop-card"><div className="bop-empty-state">Caricamento…</div></div>
      ) : !sid ? (
        <div className="bop-card">
          <div className="bop-empty-state">
            Seleziona una società: la matrice confronta gli utenti di una sola società
            per volta.
          </div>
        </div>
      ) : loading ? (
        <div className="bop-card"><div className="bop-empty-state">Caricamento matrice…</div></div>
      ) : !dati || dati.utenti.length === 0 ? (
        <div className="bop-card">
          <div className="bop-empty-state">
            Nessun utente operativo attivo su {nomeSocieta || 'questa società'}.
          </div>
        </div>
      ) : (
        <>
          <div className="bop-card">
            <div className="impostazioni-matrice-legenda">
              <span>
                {dati.moduli.length} voci × {dati.utenti.length} utenti — permesso
                “{CAMPI.find(c => c.valore === campo)?.label}”
              </span>
              <span className="impostazioni-matrice-legenda-voce">
                <span className="impostazioni-matrice-legenda-box impostazioni-matrice-legenda-box--eccezione" />
                deroga al profilo
              </span>
              <span className="impostazioni-matrice-legenda-voce">
                <span className="impostazioni-matrice-legenda-box impostazioni-matrice-legenda-box--dirty" />
                modificato, non salvato
              </span>
            </div>

            <div className="impostazioni-matrice-wrapper">
              <table className="impostazioni-matrice">
                <thead>
                  <tr>
                    <th className="impostazioni-matrice-voce">Voce di menu</th>
                    {dati.utenti.map(u => (
                      <th className="impostazioni-matrice-utente" key={u.id} title={`${u.nome} — ${u.email}`}>
                        <span className="impostazioni-matrice-utente-nome">{u.nome}</span>
                        <span className="impostazioni-matrice-utente-profilo">
                          {u.profilo?.nome ?? 'senza profilo'}
                        </span>
                        <button
                          className="bop-btn bop-btn-ghost bop-btn-sm"
                          onClick={() => applicaColonna(u.id, true)}
                          title="Concedi a questo utente su tutte le voci visibili"
                        >
                          ✓
                        </button>
                        <button
                          className="bop-btn bop-btn-ghost bop-btn-sm"
                          onClick={() => applicaColonna(u.id, false)}
                          title="Revoca a questo utente su tutte le voci visibili"
                        >
                          ✕
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => r.tipo === 'gruppo' ? (
                    <tr className="impostazioni-matrice-gruppo" key={`g${i}`}>
                      <td className="impostazioni-matrice-voce">{r.label}</td>
                      <td colSpan={dati.utenti.length} />
                    </tr>
                  ) : (
                    <tr key={r.voce.modulo}>
                      <td className="impostazioni-matrice-voce">
                        {r.voce.label}
                        <button
                          className="bop-btn bop-btn-ghost bop-btn-sm"
                          onClick={() => applicaRiga(r.voce.modulo, true)}
                          title="Concedi a tutti gli utenti in elenco"
                        >
                          ✓
                        </button>
                        <button
                          className="bop-btn bop-btn-ghost bop-btn-sm"
                          onClick={() => applicaRiga(r.voce.modulo, false)}
                          title="Revoca a tutti gli utenti in elenco"
                        >
                          ✕
                        </button>
                      </td>
                      {dati.utenti.map(u => {
                        const k         = chiave(u.id, r.voce.modulo);
                        const modificata = !!modifiche[k];
                        const origine   = dati.permessi[u.id]?.[r.voce.modulo]?.origine;
                        const classi = [
                          'impostazioni-matrice-cella',
                          modificata ? 'impostazioni-matrice-cella--dirty' : '',
                          !modificata && origine === 'eccezione' ? 'impostazioni-matrice-cella--eccezione' : '',
                        ].filter(Boolean).join(' ');

                        return (
                          <td className={classi} key={u.id}>
                            <input
                              type="checkbox"
                              className="impostazioni-permessi-checkbox impostazioni-permessi-checkbox-view"
                              checked={valoreCella(u.id, r.voce.modulo)[campo]}
                              onChange={() => toggleCella(u.id, r.voce.modulo)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="impostazioni-permessi-footer">
            <button
              className="bop-btn bop-btn-primary"
              onClick={salva}
              disabled={saving || !nModifiche}
            >
              {saving ? 'Salvo…' : `Salva ${nModifiche || ''} modifiche`.trim()}
            </button>
            {nModifiche > 0 && !saving && (
              <span className="impostazioni-permessi-msg-warning">
                {nModifiche} celle modificate, non salvate
              </span>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
